import { TestId } from '@trello/webdriverio-tests';
import classNames from 'classnames';
import forwardRefComponent from 'app/common/lib/util/forward-ref-component';
import RouterLink from 'app/common/components/singlespa/router-link';
import React from 'react';
import styles from './popover-menu.less';
import { useFocusRing } from '@trello/a11y';
import { Icon, IconName } from 'app/common/components/controls';

interface ContentProps {
  description?: string;
  rawDescription?: string;
  title?: React.ReactNode;
  icon?: IconName;
}

interface BaseProps extends ContentProps {
  appendSeparator?: boolean;
}

interface PopoverMenuButtonProps extends BaseProps {
  onClick: React.EventHandler<React.MouseEvent<HTMLButtonElement>>;
  className?: string;
  disabled?: boolean;
  testId?: TestId;
}

interface PopoverMenuLinkProps extends BaseProps {
  href: string;
  onClick?: React.EventHandler<React.MouseEvent<HTMLAnchorElement>>;
  testId?: TestId;
  className?: string;
  icon?: IconName;
}

const PopoverMenuItemContents: React.FunctionComponent<ContentProps> = ({
  children,
  description,
  rawDescription,
  title,
  icon,
}) => (
  <>
    {icon && <Icon name={icon} small />}
    <span
      className={classNames(styles.title, description && styles.withoutMargins)}
    >
      {title ? title : children}
    </span>
    {(description || rawDescription) && (
      <p className={styles.description}>
        {description ? description : rawDescription}
      </p>
    )}
  </>
);

interface PopoverMenuItemProps extends Pick<BaseProps, 'appendSeparator'> {}

const PopoverMenuItem: React.FunctionComponent<PopoverMenuItemProps> = ({
  appendSeparator,
  children,
}) => (
  <li
    className={classNames(styles.item, appendSeparator && styles.withSeparator)}
  >
    {children}
  </li>
);

export const PopoverMenuLink: React.FunctionComponent<PopoverMenuLinkProps> = ({
  appendSeparator,
  testId,
  href,
  className,
  onClick,
  ...contentProps
}) => {
  const [hasFocusRing, mouseEvents] = useFocusRing();

  return (
    <PopoverMenuItem appendSeparator={appendSeparator}>
      <RouterLink
        className={classNames(
          styles.link,
          className,
          hasFocusRing && styles.linkFocusRing,
        )}
        href={href}
        testId={testId}
        onClick={onClick}
        {...mouseEvents}
      >
        <PopoverMenuItemContents {...contentProps} />
      </RouterLink>
    </PopoverMenuItem>
  );
};

export const PopoverMenuButton = forwardRefComponent<
  HTMLButtonElement,
  PopoverMenuButtonProps
>(
  'PopoverMenuButton',
  (
    {
      appendSeparator,
      onClick,
      className,
      disabled,
      testId,
      ...contentProps
    }: PopoverMenuButtonProps,
    ref: React.RefObject<HTMLButtonElement>,
  ) => {
    const [hasFocusRing, mouseEvents] = useFocusRing();

    return (
      <PopoverMenuItem appendSeparator={appendSeparator}>
        <button
          className={classNames(
            styles.link,
            hasFocusRing && styles.linkFocusRing,
            className,
          )}
          disabled={disabled}
          onClick={onClick}
          data-test-id={testId}
          ref={ref}
          {...mouseEvents}
        >
          <PopoverMenuItemContents {...contentProps} />
        </button>
      </PopoverMenuItem>
    );
  },
);

interface PopoverMenuProps {
  className?: string;
  testId?: TestId;
}

export const PopoverMenu: React.FunctionComponent<PopoverMenuProps> = ({
  className,
  children,
  testId,
}) => (
  <nav className={className}>
    <ul data-test-id={testId}>{children}</ul>
  </nav>
);
